import React from 'react';
import { useSelector } from 'react-redux';
import { getFlightDetailsByAircraftId } from '../../redux/selectors/flyingPage';
import { SOS, SPIDERWATCH } from '../Flying/AircraftList/constants';
import { TrackPoint } from '../../common/api/spidertracks-sdk/types/TrackData';
import { getDateFormat, getTimezone } from '../../redux/selectors/userData';
import { Tooltip } from 'antd';
import { formatDisplayTime, formatDuration } from './utils/timeUtils';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en';
import { StatusIcon } from './icons/StatusIcon';
import { RED, BLUE } from './styles/constants';
import { formatSpeedToMetersPerSeconds } from '../../helpers/formatSpeed';
import { convertToMetres } from '../Flying/Map/utils/helper';
import { AircraftTypeEnum } from '../../types/flyingPage';
import {
  FIFTEEN_MINUTES,
  getStatusColourFromPointTime
} from '../../util/getStatusColourFromPointTime';

TimeAgo.addDefaultLocale(en);
const timeAgo = new TimeAgo('en-US');
interface AircraftStatusProps {
  aircraftId: string;
}

const STATIONARY_THRESHOLD_FIXED_WING_METERS_PER_SECOND = 15; // 29.1577 Knots
const STATIONARY_THRESHOLD_ROTARY_METERS_PER_SECOND = 10; // 5.14444 Knots
const STATIONARY_THRESHOLD_AGL_METERS = 15; // 50 feet;

const hasAircraftStopped = (points: TrackPoint[], aircraftType: AircraftTypeEnum): boolean => {
  let speedMetersPerSecond = 0;
  let altitudeAglMeters = 0;
  const lastPoint = points[points.length - 1];

  speedMetersPerSecond = formatSpeedToMetersPerSeconds(lastPoint.speed, lastPoint?.speedUnit);
  altitudeAglMeters = lastPoint.altitudeAgl
    ? convertToMetres(lastPoint.altitudeAgl, lastPoint?.altitudeUnit)[0]
    : 0;

  switch (aircraftType) {
    case AircraftTypeEnum.FIXED_WING:
      return (
        speedMetersPerSecond < STATIONARY_THRESHOLD_FIXED_WING_METERS_PER_SECOND &&
        altitudeAglMeters < STATIONARY_THRESHOLD_AGL_METERS
      );
    case AircraftTypeEnum.ROTARY:
    case AircraftTypeEnum.OTHER:
    case AircraftTypeEnum.UNDEFINED:
      return (
        speedMetersPerSecond < STATIONARY_THRESHOLD_ROTARY_METERS_PER_SECOND &&
        altitudeAglMeters < STATIONARY_THRESHOLD_AGL_METERS
      );
    default:
      return (
        speedMetersPerSecond < STATIONARY_THRESHOLD_ROTARY_METERS_PER_SECOND &&
        altitudeAglMeters < STATIONARY_THRESHOLD_AGL_METERS
      );
  }
};

export const StatusV2: React.FC<AircraftStatusProps> = ({ aircraftId }) => {
  let fillColour: string;
  let ringColour: string | undefined;
  let showAsStationary = false;

  const flightDetails = useSelector(getFlightDetailsByAircraftId(aircraftId));

  if (!flightDetails) {
    return <></>;
  }

  const lastPoint = flightDetails?.track.points[flightDetails.track.points.length - 1];

  if (!lastPoint) {
    return <></>;
  }

  // We've got 15 minutes since last point for watch end
  const watchEndTime = lastPoint.timestamp + FIFTEEN_MINUTES;
  const formattedSpiderWatchCountdown = formatDuration(Date.now(), watchEndTime);

  const dateFormat = useSelector(getDateFormat);
  const timeFormat = 'HH:mm:ss';
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const timezone = useSelector(getTimezone);

  const status = flightDetails?.aircraft.trackStatus;
  /* Once we have flightlogs events, we can use the power off event too */
  const isActive = flightDetails?.aircraft.isActive;

  const formattedLastPointTime = formatDisplayTime(
    lastPoint.timestamp,
    timezone,
    dateTimeFormat,
    timeFormat
  );

  fillColour = getStatusColourFromPointTime(lastPoint.timestamp);
  showAsStationary =
    !isActive ||
    hasAircraftStopped(flightDetails?.track.points || [], flightDetails?.aircraft.type);

  switch (status) {
    case SOS:
      ringColour = RED;
      break;
    case SPIDERWATCH:
      if (isActive) {
        ringColour = BLUE;
      }
      break;
  }

  return (
    <Tooltip
      placement="right"
      title={
        <span>
          Last Update: {formattedLastPointTime}
          {<br />} {timeAgo.format(lastPoint.timestamp, 'round')}
          {<br />} {status === SPIDERWATCH && `Watch Time Left: ${formattedSpiderWatchCountdown}`}
        </span>
      }
      trigger="hover"
    >
      <div style={{ display: 'inline-block' }}>
        <StatusIcon colour={fillColour} stroke={ringColour} showAsStationary={showAsStationary} />
      </div>
    </Tooltip>
  );
};
